import PropTypes from 'prop-types'
import { Fragment, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import agent from 'agent'
import Button from 'components/common/Button'
import { ModalFooterSection } from 'components/common/ConfirmationModal'
import OnLeaveModal from 'components/common/OnLeaveModal'
import RadioFieldset from 'components/common/RadioFieldset'
import Text from 'components/common/Text'
import TextArea from 'components/common/TextArea'
import { getBlastRadiusForNode } from 'components/scenarios/utils'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { formatDateTime } from 'utils/formatDate'
import { messageFromResponseError } from 'utils/strings'

import { scenarioHasStatusCheck } from '../utils/graph'

const NewResultsForm = ({
  resultFlags,
  setResultFlags,
  runInfo,
  isConfirmationModal,
  promptAddingStatusCheck
}) => {
  return (
    <div>
      <RadioFieldset
        data-test-scenario-results-expected
        css={{
          textAlign: 'left',
          marginBottom: theme.space[4]
        }}
        fieldsStyle={{
          wrapper: {
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${
              isConfirmationModal ? '120px' : '150px'
            }, 1fr))`,
            gap: theme.space[3],
            color: theme.colors.steel[2]
          },
          label: {
            gap: theme.space[2],
            marginTop: 0,
            alignItems: 'center'
          },
          legend: {
            marginBottom: theme.space[1]
          }
        }}
        fontSize={theme.fontSizes[1]}
        options={[
          {
            value: 'Passed',
            description: 'Yes (Passed)'
          },
          {
            // TODO: Figure out the value for not sure
            value: 'Unsure',
            description: "I'm Not Sure"
          },
          {
            value: 'Failed',
            description: 'No (Failed)'
          }
        ]}
        showValues={false}
        title="Did your system perform as expected?"
        value={
          resultFlags?.status === undefined
            ? runInfo?.results?.status
            : resultFlags?.status
        }
        onChange={(event, option) => {
          setResultFlags({
            ...resultFlags,
            status: option.value
          })
        }}
      />
      {promptAddingStatusCheck && (
        <Text
          as="small"
          css={{ marginBottom: theme.space[4], textAlign: 'left' }}
        >
          Consider adding Golden Signal to automate Pass/Fail results.
        </Text>
      )}
      <RadioFieldset
        data-test-scenario-results-incident
        css={{
          textAlign: 'left',
          marginBottom: theme.space[4]
        }}
        fieldsStyle={{
          label: {
            gap: theme.space[2],
            marginTop: 0,
            alignItems: 'center'
          },
          wrapper: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            color: theme.colors.steel[2]
          },
          legend: {
            marginBottom: theme.space[1]
          }
        }}
        fontSize={theme.fontSizes[1]}
        options={[
          {
            value: true,
            description: 'Yes'
          },
          {
            value: false,
            description: 'No'
          }
        ]}
        title="Was a potential incident uncovered?"
        value={
          resultFlags.incidentDetected === undefined
            ? runInfo?.results?.incidentDetected
            : resultFlags.incidentDetected
        }
        onChange={(event, option) => {
          setResultFlags({
            ...resultFlags,
            incidentDetected: option.value
          })
        }}
      />
      <RadioFieldset
        data-test-scenario-results-mitigated
        css={{
          textAlign: 'left',
          marginBottom: theme.space[4]
        }}
        fieldsStyle={{
          label: {
            gap: theme.space[2],
            marginTop: 0,
            alignItems: 'center'
          },
          wrapper: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            color: theme.colors.steel[2]
          },
          legend: {
            marginBottom: theme.space[1]
          }
        }}
        fontSize={theme.fontSizes[1]}
        options={[
          {
            value: true,
            description: 'Yes'
          },
          {
            value: false,
            description: 'No'
          }
        ]}
        title="Was an incident mitigated?"
        value={
          resultFlags.incidentMitigated === undefined
            ? runInfo?.results?.incidentMitigated
            : resultFlags.incidentMitigated
        }
        onChange={(event, option) => {
          setResultFlags({
            ...resultFlags,
            incidentMitigated: option.value
          })
        }}
      />
    </div>
  )
}

NewResultsForm.propTypes = {
  resultFlags: PropTypes.object,
  setResultFlags: PropTypes.func,
  runInfo: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  isConfirmationModal: PropTypes.bool,
  promptAddingStatusCheck: PropTypes.bool
}

function getDescription(runInfo, resultNotes, resultFlags) {
  const description = [
    `Scenario Name:\n${runInfo.name}`,
    `Scenario run link:\n${getScenarioUrl(runInfo)}`,
    `Scenario run date:\n${formatDateTime(runInfo.created_at)}`
  ]

  if (runInfo.description) {
    description.push(`Description:\n${runInfo.description}`)
  }

  description.push(`Targets/services used in test:\n${getTargets(runInfo)}`)

  if (resultFlags.status) {
    description.push(`Results:\n${resultFlags.status}`)
  }
  if (resultFlags.incidentDetected !== undefined) {
    description.push(
      `Was a potential incident uncovered?\n${
        resultFlags.incidentDetected ? 'Yes' : 'No'
      }`
    )
  }
  if (resultFlags.incidentMitigated !== undefined) {
    description.push(
      `Was an incident mitigated?\n${
        resultFlags.incidentMitigated ? 'Yes' : 'No'
      }`
    )
  }
  if (resultNotes) {
    description.push(`Result notes:\n${resultNotes}`)
  }

  return description.join('\n\n')
}

function getScenarioUrl(runInfo) {
  return `${window.location.origin}/scenarios/detail/${runInfo.scenario_id}/runs/${runInfo.run_number}`
}

function getTargets(runInfo) {
  return Object.keys(runInfo.graph?.nodes || {})
    .map((key) => {
      if (runInfo.graph.nodes[key].target_definition) {
        const radius = getBlastRadiusForNode(runInfo.graph.nodes[key])
        return `${radius.blastRadiusLabel} ${radius.blastRadiusValue}`
      }
      return ''
    })
    .filter(Boolean)
    .join('\n')
}

const ResultsForm = ({
  runInfo,
  doFetch,
  hideSave,
  onConfirm,
  footerOptions,
  onCancel,
  isConfirmationModal,
  showStatusCheckPrompt,
  isCompletedNotification,
  onSummaryDescriptionChanged
}) => {
  const history = useHistory()
  const { currentTeamId } = useAuth()
  const [resultNotes, setResultNotes] = useState(undefined)
  const [resultFlags, setResultFlags] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  useEffect(() => {
    onSummaryDescriptionChanged &&
      onSummaryDescriptionChanged(
        getDescription(runInfo, resultNotes, resultFlags)
      )
  }, [runInfo, resultNotes, resultFlags, onSummaryDescriptionChanged])

  const promptAddingStatusCheck =
    showStatusCheckPrompt && !scenarioHasStatusCheck(runInfo.graph)

  const hasNotesChanges =
    resultNotes !== undefined && resultNotes !== runInfo?.result_notes

  const checkResultFlagChange = (flag) =>
    resultFlags[flag] !== undefined &&
    runInfo?.results?.[flag] !== resultFlags[flag]

  const hasResultFlagChanges =
    checkResultFlagChange('status') ||
    checkResultFlagChange('incidentDetected') ||
    checkResultFlagChange('incidentMitigated')

  const hasChanges = hasNotesChanges || hasResultFlagChanges

  async function saveData() {
    setIsSaving(true)

    try {
      if (hasNotesChanges) {
        await agent.put(
          `/scenarios/${runInfo.scenario_id}/runs/${runInfo.run_number}/resultNotes?teamId=${currentTeamId}`,
          resultNotes,
          {
            headers: {
              'Content-Type': 'text/plain'
            }
          }
        )
      }

      if (hasResultFlagChanges) {
        const results = runInfo?.results

        const flags = Object.assign(
          {},
          {
            status: results?.status,
            incidentDetected: results?.incidentDetected,
            incidentMitigated: results?.incidentMitigated
          },
          resultFlags
        )

        await agent.put(
          `/scenarios/${runInfo.scenario_id}/runs/${runInfo.run_number}/results?teamId=${currentTeamId}`,
          JSON.stringify(flags),
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
      }
      toast({
        type: 'success',
        text: 'Results saved'
      })

      await doFetch()
      if (isConfirmationModal) {
        setResultFlags({})
        setResultNotes(undefined)
      }
      setIsSaving(false)
    } catch (err) {
      setIsSaving(false)
      return toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          "There was an issue saving this Scenario's results. Please try again later."
        )
      })
    }
  }

  return (
    <Fragment>
      <OnLeaveModal
        shouldCloseOnDiscard
        cancelText="Discard"
        confirmText="Return to results"
        isPromptedWhen={
          hasChanges && (!isCompletedNotification || !promptAddingStatusCheck)
        }
        message="You have unsaved changes to this Scenario's results."
        title="Save Scenario results?"
        onCancel={(nextLocation) => {
          setResultFlags({})
          setResultNotes(undefined)
          history.push(nextLocation.pathname)
        }}
      />
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          await saveData()
          if (onConfirm) {
            await onConfirm()
          }
        }}
      >
        <div
          css={{
            display: 'grid',
            gap: theme.space[1],
            marginBottom: theme.space[3],

            [theme.mq[0]]: {
              gridTemplateColumns: '1fr'
            }
          }}
        >
          {isConfirmationModal && (
            <Text
              as="h4"
              color="medium"
              css={{
                textAlign: 'left',
                marginTop: theme.space[2],
                marginBottom: theme.space[3]
              }}
            >
              Add Results (optional)
            </Text>
          )}
          <NewResultsForm
            isConfirmationModal={isConfirmationModal}
            promptAddingStatusCheck={promptAddingStatusCheck}
            resultFlags={resultFlags}
            runInfo={runInfo}
            setResultFlags={setResultFlags}
          />
          <div>
            <TextArea
              css={{
                marginBottom: 0
              }}
              data-test-input="results notes"
              label="Add notes and observations"
              name="resultNotes"
              value={
                resultNotes === undefined ? runInfo?.result_notes : resultNotes
              }
              onChange={(e) => setResultNotes(e.target.value)}
            />
          </div>
        </div>
        {!hideSave && (
          <Button
            primary
            disabled={!hasChanges || isSaving || isConfirming}
            type="submit"
          >
            Save
          </Button>
        )}
        {isConfirmationModal && (
          <ModalFooterSection
            disabled={isConfirming}
            promptAddingStatusCheck={promptAddingStatusCheck}
            onCancel={
              isCompletedNotification && promptAddingStatusCheck
                ? async (e) => {
                    e.preventDefault()
                    await saveData()
                    if (onConfirm) {
                      await onConfirm()
                    }
                    history.push({
                      pathname: '/scenarios/edit/hosts',
                      state: {
                        backtrack: `/scenarios/detail/${runInfo.scenario_id}`,
                        scenario: runInfo,
                        initialStepState: 'StatusCheck'
                      }
                    })
                  }
                : onCancel
            }
            onConfirm={async (e) => {
              if (isConfirming) {
                return
              }

              setIsConfirming(true)

              e.preventDefault()
              await saveData()
              if (onConfirm) {
                await onConfirm()
              }

              setIsConfirming(false)
            }}
            {...footerOptions}
            cancelText={
              isCompletedNotification
                ? promptAddingStatusCheck
                  ? 'Save and add Golden Signal'
                  : false
                : footerOptions.cancelText
            }
          />
        )}
      </form>
    </Fragment>
  )
}

ResultsForm.propTypes = {
  runInfo: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      results: PropTypes.shape({
        expectedResult: PropTypes.bool,
        incidentDetected: PropTypes.bool,
        incidentMitigated: PropTypes.bool
      }),
      result_notes: PropTypes.string,
      stage_info: PropTypes.shape({
        stage: PropTypes.string
      }),
      run_number: PropTypes.number,
      scenario_id: PropTypes.string,
      graph: PropTypes.object
    })
  ]),
  doFetch: PropTypes.func.isRequired,
  hideSave: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  hideFooter: PropTypes.bool,
  isConfirmationModal: PropTypes.bool,
  footerOptions: PropTypes.object,
  showStatusCheckPrompt: PropTypes.bool,
  isCompletedNotification: PropTypes.bool,
  onSummaryDescriptionChanged: PropTypes.func
}

ResultsForm.defaultProps = {
  onCancel: () => {},
  onConfirm: () => {},
  footerOptions: {}
}

export default ResultsForm
